import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map, catchError, firstValueFrom, of, switchMap } from 'rxjs';
import {
  FinalizeDTO,
  KeyValuePair,
  LiveForm,
  LiveFormDTO,
  LiveFormFactory,
  LiveFormSaveDTO,
  OrderData,
  OrderDTO,
  OrderFactory,
  ResourceData,
  ResourceState,
  SAVE_FORM_RESULT
} from '@lfw/roesberg.liveforms.core';
import { AppUrls } from '../../route/app-urls';
import { ErrorHandlers } from '../../shared/helpers/error-handlers';
import { ProjectsService } from '../../projects/projects.service';

@Injectable({ providedIn: 'root' })
export class OnlineFormsClient {
  private readonly http = inject(HttpClient);
  private readonly projectsService = inject(ProjectsService);

  loadForm(formId: string): Observable<LiveForm> {
    return this.http
      .get<LiveFormDTO>(AppUrls.getApiUrl(AppUrls.urlForms, 'getform') + this.formatParams('formId', formId))
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        map((dtoRaw) => {
          return LiveFormFactory.fromLiveFormDTO(dtoRaw);
        }),
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  loadFormForEdit(formId: string, force: boolean): Observable<LiveForm> {
    return this.http
      .put<LiveFormDTO>(
        AppUrls.getApiUrl(AppUrls.urlForms, 'editform') + this.formatEditParams('formId', formId, force),
        {}
      )
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        map((dtoRaw) => {
          return LiveFormFactory.fromLiveFormDTO(dtoRaw);
        }),
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  saveForm(form: LiveForm, toDelete: string[], resourcesAdd: KeyValuePair[]): Observable<SAVE_FORM_RESULT> {
    const formDto: LiveFormSaveDTO = {
      id: form.id,
      formData: form.formData,
      order: OrderFactory.toOrderDTO(form.order),
      project: form.project,
      resourcesDelete: toDelete,
      resourcesAdd,
      idsOfHiddenItems: form.idsOfHiddenItems
    };
    return this.http.put<boolean>(AppUrls.getApiUrl(AppUrls.urlForms, 'saveform'), JSON.stringify(formDto)).pipe(
      switchMap(() => {
        return of(SAVE_FORM_RESULT.SAVED_ONLINE);
      })
    );
  }

  saveFormAndCheckIn(form: LiveForm, toDelete: string[], resourcesAdd: KeyValuePair[]): Observable<SAVE_FORM_RESULT> {
    const formDto: LiveFormSaveDTO = {
      id: form.id,
      formData: form.formData,
      order: OrderFactory.toOrderDTO(form.order),
      project: form.project,
      resourcesDelete: toDelete,
      resourcesAdd,
      idsOfHiddenItems: form.idsOfHiddenItems
    };
    return this.http
      .put<boolean>(AppUrls.getApiUrl(AppUrls.urlForms, 'saveandcheckinform'), JSON.stringify(formDto))
      .pipe(
        switchMap(() => {
          // toDo: check result and return detailed  information
          return of(SAVE_FORM_RESULT.FINISHED);
          //return of(SAVE_FORM_RESULT.FINISHFAILED);
          //return of(SAVE_FORM_RESULT.SAVED_ONLINE);
        })
      );
  }

  loadResource(params: { form: string; project: string }, fileId: string): Observable<Blob> {
    return this.http.get(
      `${AppUrls.getApiUrl(AppUrls.urlForms, 'getformdata')}?projectId=${params.project}&formId=${
        params.form
      }&fileId=${fileId}`,
      { responseType: 'blob' }
    );
  }

  uploadResource(data: ResourceData, projectId: string, formId: string, file: File | Blob): Promise<ResourceData> {
    const headers = new HttpHeaders({ 'Content-Type': 'form-data' }); // will be  removed in interceptor, but avoids setting of other header
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);
    formData.append('formId', formId);

    // evtl. stream !!
    return firstValueFrom(
      this.http.post<string>(AppUrls.getApiUrl(AppUrls.urlForms, 'saveformdata'), formData, { headers }).pipe(
        map((saveId) => {
          data.state = ResourceState.Uploaded;
          data.saveId = saveId;
          return data;
        })
      )
    );
  }

  finishOrder(projectId: string, orderId: string): Observable<OrderData> {
    const dto: FinalizeDTO = {
      projectId: projectId,
      orderId: orderId
    };

    return this.http.post<OrderDTO>(AppUrls.getApiUrl(AppUrls.urlOrders, 'finishorder'), JSON.stringify(dto)).pipe(
      //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
      map((dtoRaw) => {
        return OrderFactory.fromOrderDTO(dtoRaw);
      }),
      catchError((error) => {
        return ErrorHandlers.errorHandler(error);
      })
    );
  }

  private formatEditParams(name: string, value: string, force: boolean): string {
    let param = this.formatParams(name, value);
    if (force) {
      param = param + '&forced=true';
    } else {
      param = param + '&forced=false';
    }
    return param;
  }

  private formatParams(name: string, value: string): string {
    let param = '?projectid=' + this.projectsService.currentProjectId;
    if (name != null && name.length > 0 && value != null && value.length > 0) {
      // encodeURIComponent is needed to handle special characters like # for projectids in the tree
      param = param + '&' + name + '=' + encodeURIComponent(value);
    }
    return param;
  }
}
